import type { FormEvent } from 'react'
import { Link, type RelativeRoutingType } from 'react-router-dom'

interface ButtonProps {
	readonly onClick?: ((event: FormEvent<HTMLButtonElement>) => void) | undefined;
	readonly type?: 'submit' | 'reset' | 'button' | undefined;
	readonly text?: string | undefined;
	readonly disabled?: boolean | undefined;
	readonly children?: React.ReactNode | undefined;
	readonly customClassName?: string | undefined;
	readonly style?: 'primary' | 'secondary' | 'outline' | 'outline-dark' | 'naked' | 'danger' | undefined;
	readonly size?: 'full' | 'fit' | 'auto' | undefined;
	readonly to?: string | undefined;
	readonly relative?: RelativeRoutingType | undefined;
}

export default function Button({
	onClick,
	type = 'button',
	text,
	disabled = false,
	children,
	customClassName = '',
	style = 'primary',
	size = 'full',
	to,
	relative
}: ButtonProps) {
	const defaultClassName = 'group relative flex items-center justify-center py-2 px-4 text-sm font-bold rounded-md focus:outline-none focus:ring-offset-2 transition-all duration-300 ease-in-out '
	const sizeClassName = 'w-' + size

	const styles = {
		primary: 'bg-primary hover:bg-primary-hover active:bg-primary-active text-white ',
		secondary: 'bg-secondary hover:bg-secondary-hover active:bg-secondary-active border border-blue-200 text-blue-500 ',
		outline: 'bg-transparent hover:bg-gray-50 border border-gray-400 text-gray-800 ',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'outline-dark': 'bg-transparent hover:bg-[#f9fafb0d] border border-gray-300 text-white',
		naked: 'bg-transparent hover:bg-[#f9fafb0d] text-gray-400 border border-gray-700',
		danger: 'bg-red-500 hover:bg-red-600 text-white '
	}
	const styleClassName = styles[style]

	const disableClassName = disabled ? 'opacity-20 cursor-not-allowed' : ' '

	function handleClick(event: FormEvent<HTMLButtonElement>) {
		if (disabled) {
			return
		}

		if (onClick) {
			onClick(event)
		}
	}

	if (to) {
		return (
			<Link
				to={ to }
				relative={ relative }
				target={ to.startsWith('http') ? '_blank' : '' }
				className={ `${disableClassName} ${defaultClassName} ${styleClassName} ${sizeClassName} ${customClassName}` }
			>
				{ children }
				{ text }
			</Link>
		)
	}

	return (
		<button
			type={ type }
			className={ `${disableClassName} ${defaultClassName} ${styleClassName} ${sizeClassName} ${customClassName} ` }
			onClick={ handleClick }
		>
			{ children }
			{ text }
		</button>
	)
}
